/**
 * Cart Detail Popup Manager - Moon Moon Agency
 * Gère l'ouverture/fermeture du popup "Détail commande" affiché depuis un
 * item du panier (verre personnalisé). V1 : contenu injecté via les
 * data-attributes du bouton trigger.
 *
 * Trigger : `<button data-cart-detail-open ...>` avec les data-attributes :
 *   - data-detail-label="Verre à la vue"
 *   - data-detail-brand="Anna-Karin Karlsson"
 *   - data-detail-product="Lunettes Persol PO3316S"
 *   - data-detail-image="https://..."
 *   - data-detail-options='[{"label":"Verres transparents"},{"label":"Indice 1.6"}]'
 *   - data-detail-corrections='[{"label":"Sphère","value":"0 / 0"},...]'
 *   - data-detail-prescription-url="https://..." (optionnel)
 */

/*********************************************************************************************
 * 🛒📄 CART DETAIL POPUP MANAGER
 */
const CartDetailPopupManager = {
	config: {
		selectors: {
			overlay:           '[data-cart-detail-overlay]',
			popup:             '[data-cart-detail-popup]',
			close:             '[data-cart-detail-close]',
			openTrigger:       '[data-cart-detail-open]',
			label:             '[data-cart-detail-label]',
			brand:             '[data-cart-detail-brand]',
			product:           '[data-cart-detail-product]',
			imageVerre:        '[data-cart-detail-image-verre]',
			imageMonture:      '[data-cart-detail-image-monture]',
			options:           '[data-cart-detail-options]',
			optionsSection:    '[data-cart-detail-options-section]',
			corrections:       '[data-cart-detail-corrections]',
			correctionsSection:'[data-cart-detail-corrections-section]',
			ordonnanceRow:     '[data-cart-detail-ordonnance-row]',
			prescriptionLink:  '[data-cart-detail-prescription-link]',
		},
		classes: {
			open: 'is-open',
		},
	},

	state: {
		ready: false,
		open: false,
		triggerElement: null,
		handleKeyDown: null,
	},

	init() {
		const overlay = window.getElement(this.config.selectors.overlay);
		if (!overlay) return;
		this.dom = {
			overlay,
			popup:              overlay.querySelector(this.config.selectors.popup),
			label:              overlay.querySelector(this.config.selectors.label),
			brand:              overlay.querySelector(this.config.selectors.brand),
			product:            overlay.querySelector(this.config.selectors.product),
			imageVerre:         overlay.querySelector(this.config.selectors.imageVerre),
			imageMonture:       overlay.querySelector(this.config.selectors.imageMonture),
			options:            overlay.querySelector(this.config.selectors.options),
			optionsSection:     overlay.querySelector(this.config.selectors.optionsSection),
			corrections:        overlay.querySelector(this.config.selectors.corrections),
			correctionsSection: overlay.querySelector(this.config.selectors.correctionsSection),
			ordonnanceRow:      overlay.querySelector(this.config.selectors.ordonnanceRow),
			prescriptionLink:   overlay.querySelector(this.config.selectors.prescriptionLink),
		};
		overlay.setAttribute('inert', '');
		this._bindEvents();
		this.state.ready = true;
		window.log('info', '🛒📄 [CART_DETAIL]', 'Initialized');
	},

	_bindEvents() {
		// Délégation sur tous les triggers (présents ou futurs)
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.openTrigger);
			if (trigger) {
				e.preventDefault();
				this.open(trigger);
				return;
			}
			if (e.target.closest(this.config.selectors.close)) {
				this.close();
				return;
			}
			// Click sur l'overlay (en dehors du popup) → ferme
			if (this.state.open && e.target === this.dom.overlay) {
				this.close();
			}
		});
	},

	open(triggerElement) {
		if (!this.state.ready) return;
		this.state.triggerElement = triggerElement;

		const data = triggerElement.dataset;

		// Si data-bundle-source-id est present, on lit le <script type="application/json"> adjacent
		// qui contient les properties de TOUS les nested children pour batir un recap complet du bundle.
		let optionsJson = data.detailOptions || '';
		let correctionsJson = data.detailCorrections || '';
		let prescriptionUrl = data.detailPrescriptionUrl || '';
		let labelOverride = '';

		if (data.bundleSourceId) {
			const scriptEl = document.getElementById(data.bundleSourceId);
			if (scriptEl) {
				const aggregated = this._aggregateChildren(scriptEl.textContent);
				optionsJson = JSON.stringify(aggregated.options);
				correctionsJson = aggregated.corrections || '';
				prescriptionUrl = aggregated.prescriptionUrl || prescriptionUrl;
				if (aggregated.label) labelOverride = aggregated.label;
				window.log('info', '🛒📄 [CART_DETAIL]', 'Bundle aggregated', { sourceId: data.bundleSourceId, options: aggregated.options.length });
			} else {
				window.log('warn', '🛒📄 [CART_DETAIL]', `Bundle source not found: ${data.bundleSourceId}`);
			}
		}

		/* displayLabel garde sa valeur dynamique pour l'alt de l'image verre, mais on N'ECRASE
		 * PAS le textContent du label : il reste 'Verres a la vue' (i18n) tel que rendu par
		 * le Liquid via MM_custom.cart.lens_label. */
		const displayLabel = labelOverride || data.detailLabel || '';
		if (this.dom.brand)   this.dom.brand.textContent   = data.detailBrand   || '';
		if (this.dom.product) this.dom.product.textContent = data.detailProduct || '';

		// Image verre (grande, fond) + image monture (petite, overlay devant)
		if (this.dom.imageVerre) {
			this.dom.imageVerre.innerHTML = data.detailImageVerre
				? `<img src="${this._escape(data.detailImageVerre)}" alt="${this._escape(displayLabel)}" loading="lazy">`
				: '';
		}
		if (this.dom.imageMonture) {
			this.dom.imageMonture.innerHTML = data.detailImageMonture
				? `<img src="${this._escape(data.detailImageMonture)}" alt="${this._escape(data.detailProduct || '')}" loading="lazy">`
				: '';
		}

		// Options : juste les labels (avec text/font en sous pour gravure)
		this._renderList(this.dom.options, this.dom.optionsSection, optionsJson, (opt) => `
			<div class="mm-cart-detail-option">
				<svg class="mm-cart-detail-option-check" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M11.375 4.375L6.12501 9.625L3.39136 6.89103" stroke="#0B0B0B" stroke-linecap="square" stroke-linejoin="bevel"/>
				</svg>
				<span class="text-sm">
					${this._escape(opt.label || '')}${opt.text ? ` : <strong>${this._escape(opt.text)}</strong>` : ''}${opt.font ? ` <em style="opacity:.6;">(${this._escape(opt.font)})</em>` : ''}
				</span>
			</div>
		`);

		// Corrections : prescription object -> rows OU array [{label,value}]
		const correctionsRows = this._parseCorrections(correctionsJson);
		const hasCorrections = correctionsRows.length > 0;
		const hasPrescriptionUrl = !!prescriptionUrl;

		// Ordonnance row : visible uniquement si URL presente
		if (this.dom.ordonnanceRow) {
			this.dom.ordonnanceRow.hidden = !hasPrescriptionUrl;
		}
		if (this.dom.prescriptionLink && hasPrescriptionUrl) {
			this.dom.prescriptionLink.href = prescriptionUrl;
		}

		// Section CORRECTIONS : visible si corrections OU prescription URL
		this._renderList(this.dom.corrections, this.dom.correctionsSection, JSON.stringify(correctionsRows), (row) => `
			<div class="mm-cart-detail-row">
				<span class="mm-cart-detail-row-label text-sm">${this._escape(row.label || '')}</span>
				<span class="text-sm">${this._escape(row.value || '')}</span>
			</div>
		`);
		// Override : si pas de corrections rows MAIS prescription URL, on garde la section ouverte
		if (this.dom.correctionsSection) {
			this.dom.correctionsSection.hidden = !(hasCorrections || hasPrescriptionUrl);
		}

		// Affichage
		this.dom.overlay.removeAttribute('inert');
		this.dom.overlay.hidden = false;
		// Reflow forcé pour permettre la transition CSS
		void this.dom.overlay.offsetWidth;
		this.dom.overlay.classList.add(this.config.classes.open);
		this.dom.overlay.setAttribute('aria-hidden', 'false');
		this.state.open = true;
		document.documentElement.classList.add('stop-scrolling');

		// Escape ferme + restaure focus
		this.state.handleKeyDown = (ev) => {
			if (ev.key === 'Escape') {
				ev.preventDefault();
				this.close();
			}
		};
		document.addEventListener('keydown', this.state.handleKeyDown);

		setTimeout(() => this.dom.popup?.focus?.(), 100);
		window.emit('global:cart-detail-opened');
	},

	close() {
		if (!this.state.open) return;
		this.dom.overlay.classList.remove(this.config.classes.open);
		this.dom.overlay.setAttribute('aria-hidden', 'true');
		this.dom.overlay.setAttribute('inert', '');
		this.state.open = false;
		document.documentElement.classList.remove('stop-scrolling');

		if (this.state.handleKeyDown) {
			document.removeEventListener('keydown', this.state.handleKeyDown);
			this.state.handleKeyDown = null;
		}

		// Cache après transition
		setTimeout(() => {
			if (!this.state.open) this.dom.overlay.hidden = true;
		}, 350);

		this.state.triggerElement?.focus?.();
		this.state.triggerElement = null;
		window.emit('global:cart-detail-closed');
	},

	/**
	 * Agrège les properties de tous les nested children du bundle.
	 * Input : JSON string `[{ _lens_type, _lens_options, ..., _engraving_color, _engraving_zones }, ...]`
	 * Output : { options: [...], corrections: '...', prescriptionUrl: '...', label: '...' }
	 *
	 * - options : concat des _lens_options + zones de gravure (avec prefix "Gravure {color}")
	 * - corrections : premier _lens_prescription rencontre (objet prescription brut)
	 * - prescriptionUrl : premier _lens_prescription_url
	 * - label : premier _lens_type (sinon "Gravure {color}" si seulement gravure)
	 */
	_aggregateChildren(json) {
		const result = { options: [], corrections: '', prescriptionUrl: '', label: '' };
		let children;
		try { children = JSON.parse(json); }
		catch (_) { return result; }
		if (!Array.isArray(children)) return result;

		// Shopify HTML-encode les valeurs de line item properties (&quot; au lieu de ").
		// On decode avant de JSON.parse les strings imbriquees.
		const decoder = document.createElement('textarea');
		const decodeHtml = (str) => {
			if (!str) return '';
			decoder.innerHTML = String(str);
			return decoder.value;
		};
		const parseInner = (str) => {
			try { return JSON.parse(decodeHtml(str)); }
			catch (_) { return null; }
		};

		/* Premier pass : detecte si un child porte des _engraving_zones avec contenu.
		 * Utilisé pour eviter le doublon "Diamond cut + Gravure Transparente" quand
		 * un art_detail mene a la gravure (les zones detaillees rendent le label
		 * generique redondant). */
		let hasEngravingZones = false;
		for (const ch of children) {
			if (!ch._engraving_zones) continue;
			const z = parseInner(ch._engraving_zones);
			if (Array.isArray(z) && z.length > 0) { hasEngravingZones = true; break; }
		}

		for (const ch of children) {
			if (ch._lens_type && !result.label) result.label = ch._lens_type;

			if (ch._lens_options) {
				const opts = parseInner(ch._lens_options);
				if (Array.isArray(opts)) result.options.push(...opts);
			}

			if (ch._lens_prescription && !result.corrections) {
				result.corrections = decodeHtml(ch._lens_prescription);
			}
			if (ch._lens_prescription_url && !result.prescriptionUrl) {
				result.prescriptionUrl = decodeHtml(ch._lens_prescription_url);
			}

			/* Art du detail SANS gravure detaillee (ex: Diamond cut, Insertion diamant).
			 * Si une gravure avec zones est presente, on prefere afficher les zones
			 * (qui sont ajoutees plus bas) plutot que le label generique. */
			if (ch._art_detail_label && !hasEngravingZones) {
				result.options.push({
					label: decodeHtml(ch._art_detail_label),
					text: '',
					font: null,
				});
			}

			if (ch._engraving_zones) {
				const color = ch._engraving_color || 'transparent';
				const zones = parseInner(ch._engraving_zones);
				/* Label traduit lu depuis data-i18n-gravure sur [data-cart-detail-popup] (i18n via Liquid) */
				const popupEl = document.querySelector('[data-cart-detail-popup]');
				const gravureLbl = popupEl?.dataset.i18nGravure || 'Gravure';
				if (Array.isArray(zones)) {
					for (const z of zones) {
						result.options.push({
							label: `${gravureLbl} ${color}${z.label ? ` — ${z.label}` : ''}`,
							text: z.text || '',
							/* Prefere le nom lisible (font_name = 'Impact') au code CSS
							 * (font = 'aa4'). Fallback sur font pour compatibilite avec
							 * les commandes anciennes qui n'ont pas encore font_name. */
							font: z.font_name || z.font || null,
						});
					}
				}
				if (!result.label) result.label = `${gravureLbl} ${color}`;
			}
		}
		return result;
	},

	/**
	 * Accepte un JSON brut au format prescription {od, og, addition, distance_pupillaire}
	 * et le convertit en [{label, value}] pour affichage.
	 * Si c'est deja un array, renvoie tel quel.
	 */
	_parseCorrections(json) {
		if (!json) return [];
		/* Marqueur string brut (parcours sans-correction) : on l'affiche tel quel
		 * dans une seule ligne, label vide, valeur = la string. Ex: "Sans correction". */
		if (typeof json === 'string' && !json.startsWith('{') && !json.startsWith('[')) {
			return [{ label: '', value: json }];
		}
		let parsed;
		try { parsed = JSON.parse(json); }
		catch (_) { return []; }

		if (Array.isArray(parsed)) return parsed;

		if (parsed && typeof parsed === 'object') {
			const rows = [];
			const fmt = (od, og) => `${od ?? 0} / ${og ?? 0}`;
			const od = parsed.od || {};
			const og = parsed.og || {};
			/* Labels traduits lus depuis data-i18n-* sur [data-cart-detail-popup] (i18n via Liquid) */
			const popupEl = document.querySelector('[data-cart-detail-popup]');
			const sphereLbl   = popupEl?.dataset.i18nSphere   || 'Sphère';
			const cylindreLbl = popupEl?.dataset.i18nCylindre || 'Cylindre';
			const axeLbl      = popupEl?.dataset.i18nAxe      || 'Axe';
			const additionLbl = popupEl?.dataset.i18nAddition || 'ADD';
			rows.push({ label: sphereLbl,   value: fmt(od.sphere, og.sphere) });
			rows.push({ label: cylindreLbl, value: fmt(od.cylindre, og.cylindre) });
			rows.push({ label: axeLbl,      value: fmt(od.axe, og.axe) });
			if (parsed.addition) rows.push({ label: additionLbl, value: String(parsed.addition) });
			if (parsed.distance_pupillaire) {
				const dpLabel = popupEl?.dataset.i18nDistancePupillaire || 'Distance pupillaire';
				rows.push({ label: dpLabel, value: `${parsed.distance_pupillaire} mm` });
			}
			return rows;
		}
		return [];
	},

	_renderList(container, section, json, itemTemplate) {
		if (!container || !section) return;
		let arr = [];
		try {
			arr = json ? JSON.parse(json) : [];
		} catch (e) {
			window.log('warn', '🛒📄 [CART_DETAIL]', 'Invalid JSON in data-attribute', { json });
		}
		if (!Array.isArray(arr) || arr.length === 0) {
			section.hidden = true;
			container.innerHTML = '';
			return;
		}
		section.hidden = false;
		container.innerHTML = arr.map(itemTemplate).join('');
	},

	_escape(str) {
		return String(str ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	},
};

// Auto-init
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => CartDetailPopupManager.init());
} else {
	CartDetailPopupManager.init();
}

window.MMCartDetailPopup = CartDetailPopupManager;
